import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Tag } from '@arco-design/web-react';
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import { get } from 'lodash';

import { IconFont } from '@/components';
import { buildNicSummaryLines } from '@/components-biz/NicConfiguration';
import type { MaasGroupSpec } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import {
  getMaasGroupInitCommandDisplay,
  getMaasGroupStartupCommandDisplay,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/display';
import {
  hasMeaningfulSharedMemoryConfig,
  readSharedMemoryFromFormValues,
  sharedMemoryFromSystemDevices,
} from '@/delivery-type-plugin/capabilities/shared-memory';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import { formatVendorDeviceCellLabel } from '@/delivery-type-plugin/components/vendorAndDeviceRadioList';
import type { NicConfigurationValue } from '@/types/nic-configuration';

import type { EmbeddedNodeSpec } from '../nodeFields';
import { readEmbeddedNodeSystemDiskGi, resolveEmbeddedSharedMemoryEnabled } from '../nodeFields';
import { CardSubtitleTooltip } from './CardSubtitleTooltip';
import { ReadonlyCommandEllipsisRow } from './ReadonlyCommandEllipsisRow';
import styles from './group-spec-card.module.less';
import { GroupSpecEmbeddedPreview } from './group-spec-embedded-preview';

interface GroupSpecCardProps {
  role: 'master' | 'worker';
  /** Master 节点卡片的业务角色标签。 */
  masterRoleLabel?: string;
  spec?: MaasGroupSpec;
  /** 节点关联的系统盘大小（Gi），预览/详情并入节点卡片展示。 */
  systemDiskGi?: number;
  onEdit: () => void;
  onDelete?: () => void;
  readonly?: boolean;
  /** 展示节点内嵌镜像 / 服务 / 探活 / 共享内存 / 网卡摘要 */
  embeddedPreview?: boolean;
  trainingImageOnly?: boolean;
  /** MI/TC：展示节点级启动命令（system.command/args） */
  showNodeStartupCommand?: boolean;
}

interface MetricItem {
  key: string;
  icon: string;
  label: string;
  value: string;
}

/** 资源展示：厂商-设备 * 数量（GENERIC_VENDOR / GENERIC_DEVICE → 通用） */
export function formatResourceDisplay(textList: string[] | undefined, quantity?: string | number | undefined): string {
  const qty = quantity === undefined || quantity === null || quantity === '' ? '' : String(quantity);
  const name = (textList ?? [])
    .map((text) => (text ? formatVendorDeviceCellLabel(String(text)) : ''))
    .filter(Boolean)
    .join(' / ');
  if (!qty) return name;
  if (!name) return qty;
  return `${name} * ${qty}`;
}

function buildMetrics(spec: MaasGroupSpec, t: (key: string) => string, systemDiskGi?: number): MetricItem[] {
  const cpu = spec.resource?.cpu as Record<string, unknown> | undefined;
  const memory = spec.resource?.memory as Record<string, unknown> | undefined;
  const gpu = spec.resource?.gpu as Record<string, unknown> | undefined;
  const cpuQty = cpu?.request ?? cpu?.cores;
  const memQty = get(memory, 'request') ?? get(memory, 'resource.memory.request');
  const gpuQty = get(gpu, 'number') ?? get(gpu, 'card.request') ?? get(gpu, 'resource.nvidia.com/gpu.request');

  const metrics: MetricItem[] = [
    {
      key: 'replicas',
      icon: 'icon-fuben',
      label: t('data-center:maas.node.count'),
      value: String(spec.replicas ?? 1),
    },
    {
      key: 'cpu',
      icon: 'icon-cpu',
      label: t('data-center:cpu.resource'),
      value: formatResourceDisplay(
        [cpu?.vendor as string | undefined, cpu?.device as string | undefined],
        cpuQty as string | number | undefined,
      ),
    },
    {
      key: 'memory',
      icon: 'icon-memory',
      label: t('common:memory'),
      value: formatResourceDisplay(
        [memory?.vendor as string | undefined, memory?.device as string | undefined],
        memQty as string | number | undefined,
      ),
    },
  ];

  if (typeof systemDiskGi === 'number' && systemDiskGi > 0) {
    metrics.push({
      key: 'systemDisk',
      icon: 'icon-yunpan',
      label: t('common:system.disk.size'),
      value: `${systemDiskGi} Gi`,
    });
  }

  const embeddedUi = spec as EmbeddedNodeSpec;
  const system = spec.system as Record<string, unknown> | undefined;
  const devices = system?.devices as Record<string, unknown> | undefined;
  if (resolveEmbeddedSharedMemoryEnabled(embeddedUi)) {
    const sharedMemory =
      readSharedMemoryFromFormValues({ _containerDeploySharedMemory: embeddedUi._containerDeploySharedMemory }) ??
      sharedMemoryFromSystemDevices(devices);
    const sizeLimit = String(sharedMemory?.sizeLimit ?? '').trim();
    if (hasMeaningfulSharedMemoryConfig(sharedMemory) && sizeLimit) {
      metrics.push({
        key: 'sharedMemory',
        icon: 'icon-memory',
        label: t('data-center:shared.memory'),
        value: sizeLimit,
      });
    }
  }

  if (gpuQty !== undefined && gpuQty !== null && gpuQty !== '') {
    metrics.push({
      key: 'gpu',
      icon: 'icon-GPU',
      label: t('common:graphics.card'),
      value: `${formatResourceDisplay(
        [gpu?.vendor as string | undefined, gpu?.device as string | undefined],
        gpuQty as string | number | undefined,
      )} / ${formatResourceDisplay([gpu?.driverVersion as string | undefined, gpu?.cudaVersion as string | undefined])}`,
    });
  }

  const resource = spec.resource as { nics?: NicConfigurationValue; network?: { nics?: NicConfigurationValue } } | undefined;
  const nics = resource?.nics ?? resource?.network?.nics;
  if (embeddedUi._nicEnabled !== false && nics?.length) {
    const nicLines = buildNicSummaryLines(nics);
    if (nicLines.length) {
      metrics.push({
        key: 'nic',
        icon: 'icon-wangluo',
        label: t('data-center:network.card'),
        value: nicLines.join('; '),
      });
    }
  }

  return metrics;
}

function GroupSpecCardMetrics({ items }: { items: MetricItem[] }) {
  return (
    <div className={styles.cardMetrics}>
      {items.map((item) => (
        <div key={item.key} className={styles.metricItem} title={`${item.label}: ${item.value}`}>
          <IconFont name={item.icon} style={{ fontSize: 14, color: 'var(--color-text-3)' }} />
          <span className={styles.metricLabel}>{item.label}</span>
          <span className={styles.metricValue}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export function GroupSpecCard({
  role,
  masterRoleLabel,
  spec,
  systemDiskGi,
  onEdit,
  onDelete,
  readonly,
  embeddedPreview,
  trainingImageOnly,
  showNodeStartupCommand = false,
}: GroupSpecCardProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const effectiveReadonly = readonly || sectionReadOnly;
  const isMaster = role === 'master';
  const resolvedMasterRoleLabel = masterRoleLabel ?? t('data-center:maas.master.group.badge');

  if (!spec) {
    return (
      <div
        className={styles.placeholderCard}
        role="button"
        tabIndex={effectiveReadonly ? -1 : 0}
        onClick={effectiveReadonly ? undefined : onEdit}
        onKeyDown={
          effectiveReadonly
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onEdit();
                }
              }
        }
      >
        <IconFont name="icon-rongqijiuxu" style={{ fontSize: 36, color: 'var(--color-text-4)' }} />
        <span className={styles.placeholderTitle}>
          {isMaster
            ? t('data-center:maas.master.group.placeholder', { role: resolvedMasterRoleLabel })
            : t('data-center:maas.worker.group.placeholder')}
        </span>
        <span className={styles.placeholderDesc}>
          {isMaster
            ? t('data-center:maas.master.group.placeholder.desc', { role: resolvedMasterRoleLabel })
            : t('data-center:maas.worker.group.placeholder.desc')}
        </span>
      </div>
    );
  }

  const title = spec.name || (role === 'master' ? t('data-center:maas.master.group') : t('data-center:maas.worker.group'));
  const initCommandDisplay = getMaasGroupInitCommandDisplay(spec);
  const startupCommandDisplay = showNodeStartupCommand ? getMaasGroupStartupCommandDisplay(spec) : '-';
  const showStartupSubtitle = showNodeStartupCommand && startupCommandDisplay !== '-';
  const metrics = buildMetrics(spec, t, systemDiskGi ?? readEmbeddedNodeSystemDiskGi(spec));

  return (
    <div className={styles.card}>
      <div className={styles.cardMain}>
        <div className={styles.cardIdentity}>
          <div className={`${styles.cardIcon} ${isMaster ? '' : styles.cardIconWorker}`}>
            <IconFont
              name="icon-rongqijiuxu"
              style={{
                fontSize: 24,
                color: isMaster ? 'rgb(var(--primary-6))' : 'rgb(var(--cyan-6))',
              }}
            />
          </div>
          <div className={styles.cardMeta}>
            <div className={styles.cardTitleRow}>
              <span className={styles.cardName} title={title}>
                {title}
              </span>
              <Tag size="small" color={isMaster ? 'arcoblue' : 'cyan'}>
                {isMaster ? resolvedMasterRoleLabel : t('data-center:maas.worker.group.badge')}
              </Tag>
              {isMaster && !effectiveReadonly && (
                <Tag size="small" color="orangered">
                  {t('common:required')}
                </Tag>
              )}
            </div>
            {effectiveReadonly ? (
              <>
                <div className={styles.cardSubtitleReadonly}>
                  <span className={styles.cardSubtitleLabel}>{t('data-center:maas.init.command')}:</span>
                  <span className={styles.cardSubtitleCommand}>{initCommandDisplay}</span>
                </div>
                {showStartupSubtitle ? (
                  <ReadonlyCommandEllipsisRow label={t('components:startup.command')} fullText={startupCommandDisplay} modalTitle={title} />
                ) : null}
              </>
            ) : (
              <>
                <CardSubtitleTooltip tooltip={`${t('data-center:maas.init.command')}: ${initCommandDisplay}`}>
                  {t('data-center:maas.init.command')}: {initCommandDisplay}
                </CardSubtitleTooltip>
                {showStartupSubtitle ? (
                  <CardSubtitleTooltip tooltip={`${t('components:startup.command')}: ${startupCommandDisplay}`}>
                    {t('components:startup.command')}: {startupCommandDisplay}
                  </CardSubtitleTooltip>
                ) : null}
              </>
            )}
          </div>
        </div>
        {!effectiveReadonly && (
          <div className={styles.cardActions}>
            <Button type="text" size="small" icon={<IconEdit />} onClick={onEdit}>
              {t('common:edit')}
            </Button>
            {role === 'worker' && onDelete && (
              <Button type="text" size="small" status="danger" icon={<IconDelete />} onClick={onDelete}>
                {t('common:delete')}
              </Button>
            )}
          </div>
        )}
      </div>
      <GroupSpecCardMetrics items={metrics} />
      {embeddedPreview ? <GroupSpecEmbeddedPreview spec={spec} trainingImageOnly={trainingImageOnly} /> : null}
    </div>
  );
}

interface GroupAddCardProps {
  onClick: () => void;
}

export function GroupAddCard({ onClick }: GroupAddCardProps) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  if (sectionReadOnly) return null;
  return (
    <div
      className={styles.addCard}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <IconPlus className={styles.addIcon} />
      <span>{t('data-center:maas.add.worker')}</span>
    </div>
  );
}

/** 确认/详情页只读展示 Worker 列表（无添加按钮） */
export function WorkerGroupConfirmList({
  workers,
  workerSystemDiskGi,
  embeddedPreview,
  trainingImageOnly,
  showNodeStartupCommand,
}: {
  workers: MaasGroupSpec[];
  /** Worker 节点组共用的系统盘大小（Gi） */
  workerSystemDiskGi?: number;
  embeddedPreview?: boolean;
  trainingImageOnly?: boolean;
  showNodeStartupCommand?: boolean;
}) {
  const { t } = useTranslation();
  if (!workers.length) {
    return <span className={styles.sectionHint}>{t('data-center:maas.deploy.no.worker')}</span>;
  }
  return (
    <div className={styles.cardList}>
      {workers.map((worker, index) => (
        <GroupSpecCard
          key={`confirm-worker-${worker.name}-${index}`}
          role="worker"
          spec={worker}
          systemDiskGi={readEmbeddedNodeSystemDiskGi(worker) ?? workerSystemDiskGi}
          onEdit={() => undefined}
          readonly
          embeddedPreview={embeddedPreview}
          trainingImageOnly={trainingImageOnly}
          showNodeStartupCommand={showNodeStartupCommand}
        />
      ))}
    </div>
  );
}

/** 节点配置区块标题 */
export function GroupSpecSection({
  title,
  hint,
  children,
  style,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div className={styles.section} style={style}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{title}</span>
        {hint ? <span className={styles.sectionHint}>{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}
